import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronLeft, FileCheck2, Send, ShieldCheck, X, Zap } from "lucide-react";import AppShell from "../components/AppShell";
import AutocompleteInput from "../components/AutocompleteInput";
import { api } from "../lib/api";

function InvoiceAllowanceBar({ allowance, onAddonBought }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const pct = allowance.plan_limit ? Math.min(100, Math.round((allowance.used / allowance.plan_limit) * 100)) : 0;
  const nearLimit = allowance.plan_limit && allowance.used >= (allowance.warn_threshold || allowance.plan_limit);
  const buyAddon = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoice-addon/purchase", { back_url: window.location.href }, { withCredentials: true });
      if (data.init_point) window.location.href = data.init_point;
    } catch (_e) {}
    finally { setBusy(false); }
  };
  return (
    <section className="invoice-allowance" data-testid="invoice-allowance-bar" style={{
      padding: "14px 16px", border: "1px solid #e5e7eb", borderRadius: 14, background: nearLimit ? "#fffbeb" : "#fff", marginBottom: 14,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <b data-testid="allowance-summary">{allowance.used} de {allowance.plan_limit} utilizadas</b>
          {allowance.extra_credits > 0 && <span style={{ marginLeft: 8, color: "#4169e1", fontWeight: 700, fontSize: 12 }}>+ {allowance.extra_credits} adicionales</span>}
          <small style={{ display: "block", color: "#6b7280", fontSize: 12 }}>
            {allowance.plan === "trial" ? "Prueba gratis · 1 factura incluida" : `Plan ${allowance.plan} · ciclo hasta ${allowance.cycle_end || "—"}`}
          </small>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={buyAddon} disabled={busy} data-testid="invoice-addon-buy" style={{ padding: "8px 12px", background: "#4169e1", color: "#fff", border: 0, borderRadius: 9, fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
            <Zap size={12} /> {busy ? "..." : "+100 facturas · $500"}
          </button>
          <button type="button" onClick={() => navigate("/pricing")} data-testid="invoice-upgrade-plan" style={{ padding: "8px 12px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 9, fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
            Cambiar plan
          </button>
        </div>
      </div>
      <div style={{ marginTop: 10, height: 6, background: "#f3f4f6", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "#dc2626" : pct >= 80 ? "#f59e0b" : "#4169e1", transition: "width .3s" }} />
      </div>
      {nearLimit && allowance.remaining > 0 && (
        <p data-testid="allowance-warning" style={{ margin: "8px 0 0", color: "#854d0e", fontSize: 12, fontWeight: 700 }}>
          <AlertTriangle size={12} /> Has usado {allowance.used} de {allowance.plan_limit} facturas. Te quedan {allowance.remaining}.
        </p>
      )}
    </section>
  );
}

function FiscalBlockModal({ detail, onClose }) {
  const navigate = useNavigate();
  const isTrial = detail.code === "TRIAL_INVOICE_LIMIT_REACHED";
  const isLimit = detail.code === "INVOICE_LIMIT_REACHED";
  const needsProfile = detail.code === "FISCAL_PROFILE_INCOMPLETE" || detail.code === "FISCAL_CONFIRMATION_REQUIRED";
  const needsCsd = detail.code === "CSD_MISSING";
  const needsEfirma = detail.code === "EFIRMA_MISSING";
  const needsManifest = detail.code === "MANIFEST_UNSIGNED";
  const needsFiscal = needsProfile || needsCsd || needsEfirma || needsManifest;
  const title = isTrial ? "Ya utilizaste tu factura de prueba"
    : isLimit ? "Alcanzaste tu límite de facturas"
    : needsFiscal ? "No puedes emitir facturas todavía" : "No podemos emitir esta factura";
  const cta = needsFiscal ? { label: "Completar Perfil Fiscal", to: "/perfil-negocio?tab=fiscal" }
    : { label: "Ver planes", to: "/pricing" };
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="fiscal-block-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <header>
          <div><small>{detail.code}</small><b>{title}</b></div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </header>
        <div style={{ padding: "6px 24px 22px" }}>
          <p style={{ color: "#4b5563", margin: "0 0 14px" }}>{detail.message}</p>
          {needsFiscal && (
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 14px" }}>
              {[
                { label: "RFC + Datos fiscales completos", ok: detail.gates?.profile_complete },
                { label: "Certificado de Sello Digital (CSD)", ok: detail.gates?.csd_valid },
                { label: "e.firma cifrada", ok: detail.gates?.efirma_valid },
                { label: "Carta Manifiesto firmada", ok: detail.gates?.manifest_signed },
              ].map((k) => (
                <li key={k.label} style={{ padding: "6px 0", color: k.ok ? "#065f46" : "#7f1d1d", fontSize: 13, fontWeight: 700 }}>
                  {k.ok ? "✓" : "☐"} {k.label}
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="cash-btn ghost" onClick={onClose}>Cancelar</button>
            <button type="button" className="cash-btn primary" onClick={() => navigate(cta.to)} data-testid="fiscal-block-cta">
              {cta.label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const emptyForm = {
  receiver_name: "",
  receiver_rfc: "",
  receiver_email: "",
  receiver_tax_system: "601",
  receiver_zip: "",
  usage_code: "G03",
  payment_form: "03",
  payment_method: "PUE",
  description: "",
  product_key: "84111506",
  unit_key: "E48",
  quantity: "1",
  unit_price: "",
};

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });

const GATE_LABELS = {
  profile_complete: "Datos fiscales (RFC, razón social, régimen, CP)",
  csd_valid: "Certificado de Sello Digital (CSD)",
  efirma_valid: "e.firma cifrada",
  manifest_signed: "Carta Manifiesto firmada",
};

export default function BillingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [proforma, setProforma] = useState(null);
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState(null);
  const [stamping, setStamping] = useState(false);
  const [result, setResult] = useState("");
  const [business, setBusiness] = useState(null);
  const [clients, setClients] = useState([]);
  const [allowance, setAllowance] = useState(null);
  const [validation, setValidation] = useState({ ready: true, gates: {}, block_reason: null, block_message: "" });

  const refreshClients = () => api.get("/clients", { withCredentials: true }).then((r) => setClients(r.data || [])).catch(() => setClients([]));
  const refreshAllowance = () => api.get("/invoice-allowance", { withCredentials: true }).then((r) => setAllowance(r.data)).catch(() => setAllowance(null));
  const refreshValidation = () => api.get("/business-profile/invoice-defaults", { withCredentials: true })
    .then((r) => setValidation({ ready: r.data.ready, gates: r.data.gates || {}, block_reason: r.data.block_reason, block_message: r.data.block_message || "" }))
    .catch(() => {});

  useEffect(() => {
    api.get("/business-profile", { withCredentials: true }).then((response) => setBusiness(response.data)).catch(() => setBusiness(null));
    refreshClients();
    refreshAllowance();
    refreshValidation();
  }, []);

  const pickClient = (name) => {
    const match = clients.find((client) => (client.name || "").toLowerCase() === name.toLowerCase());
    setForm((current) => ({
      ...current,
      receiver_name: name,
      ...(match ? {
        receiver_rfc: match.rfc || current.receiver_rfc,
        receiver_email: match.email || current.receiver_email,
      } : {}),
    }));
  };

  const createClient = async (name) => {
    try {
      const response = await api.post("/clients", { name }, { withCredentials: true });
      await refreshClients();
      setForm((current) => ({ ...current, receiver_name: response.data.name }));
      return response.data.name;
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos crear el cliente.");
      return null;
    }
  };

  const subtotal = Number(form.quantity || 0) * Number(form.unit_price || 0);
  const iva = subtotal * 0.16;
  const update = (event) => setForm({ ...form, [event.target.name]: event.target.value });

  async function makeProforma(event) {
    event.preventDefault();
    setError(""); setErrorDetail(null);
    if (!validation.ready) {
      setErrorDetail({ code: validation.block_reason || "FISCAL_PROFILE_INCOMPLETE", message: validation.block_message || "No puedes emitir facturas todavía. Completa tu Perfil Fiscal y firma tu Carta Manifiesto para habilitar la facturación." });
      setError(validation.block_message || "No puedes emitir facturas todavía.");
      return;
    }
    try {
      const response = await api.post("/billing/proforma", {
        ...form,
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price),
        item: {
          description: form.description,
          product_key: form.product_key,
          unit_key: form.unit_key,
          quantity: Number(form.quantity),
          unit_price: Number(form.unit_price),
        },
      });
      setProforma(response.data);
      setResult("");
      refreshAllowance();
    } catch (issue) {
      const detail = issue.response?.data?.detail;
      if (issue.response?.status === 402 && typeof detail === "object") {
        setErrorDetail(detail);
        setError(detail.message || "No podemos emitir esta factura.");
        refreshValidation();
      } else {
        setError(typeof detail === "string" ? detail : "Revisa los datos de la factura.");
      }
    }
  }

  async function stampInvoice() {
    if (!proforma) return;
    setStamping(true);
    setError("");
    try {
      const response = await api.post(`/billing/${proforma.id}/stamp`);
      setProforma(response.data);
      setResult(response.data.message);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos emitir la factura.");
    } finally {
      setStamping(false);
    }
  }

  if (proforma) {
    return <AppShell><div className="page billing-page">
      <section className="simple-intro billing-intro">
        <small className="eyebrow">REVISA ANTES DE EMITIR</small>
        <h1 data-testid="proforma-heading">Tu <em>pro-forma.</em></h1>
        <p data-testid="proforma-intro">Aquí está tu factura sin timbrar. Cuando todo se vea bien, la emitimos.</p>
      </section>
      <section className="proforma-sheet" data-testid="proforma-preview">
        <div className="proforma-top"><div><span className="blue-dot"></span><b>{business?.business_name || "Mi Contador"}</b><small>{business?.rfc ? `${business.rfc} · ` : ""}Factura pro-forma · {proforma.folio}</small></div><span className="proforma-badge" data-testid="proforma-status">SIN TIMBRAR</span></div>
        <div className="proforma-parties"><div><small>PARA</small><strong data-testid="proforma-client-name">{proforma.receiver_name}</strong><p>{proforma.receiver_rfc}<br />{proforma.receiver_email}</p></div><div><small>CONCEPTO</small><strong data-testid="proforma-description">{proforma.item.description}</strong><p>{proforma.item.quantity} × {money.format(proforma.item.unit_price)}<br />Uso CFDI: {form.usage_code}</p></div></div>
        <div className="proforma-total"><span>Subtotal</span><b>{money.format(proforma.subtotal)}</b><span>IVA (16%)</span><b>{money.format(proforma.iva)}</b><strong>Total</strong><strong data-testid="proforma-total">{money.format(proforma.total)}</strong></div>
      </section>
      {error && <p className="billing-error" data-testid="billing-error">{typeof error === "string" ? error : "No pudimos completar la solicitud."}</p>}
      {result && <p className="billing-success" data-testid="billing-success"><CheckCircle2 size={18} />{result}</p>}
      <div className="billing-actions"><button className="plain" onClick={() => setProforma(null)} data-testid="back-to-billing-form-button"><ChevronLeft size={18} />Editar datos</button><button className="emit-button" onClick={stampInvoice} disabled={stamping || proforma.status === "valid"} data-testid="emit-invoice-button"><Send size={20} />{stamping ? "Emitiendo..." : proforma.status === "valid" ? "Factura emitida" : "Emitir Factura"}</button></div>
      <p className="billing-note" data-testid="billing-stamp-note"><ShieldCheck size={16} />Al emitir, Facturapi timbra oficialmente y envía PDF/XML al correo del cliente.</p>
    </div></AppShell>;
  }

  return <AppShell><div className="page billing-page">
    <section className="simple-intro billing-intro"><small className="eyebrow">FACTURACIÓN OFICIAL</small><h1 data-testid="billing-heading">Factura sin<br /><em>complicarte.</em></h1><p data-testid="billing-intro">Llena lo esencial. Primero verás una pro-forma y tú decides cuándo emitir.</p></section>
    {!validation.ready && (
      <section className="fiscal-gate-banner" data-testid="fiscal-gate-banner" style={{
        padding: "16px 18px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 14,
        marginBottom: 16, display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      }}>
        <AlertTriangle size={22} color="#dc2626" style={{ flex: "0 0 auto", marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <b style={{ display: "block", font: '800 14px "Nunito", sans-serif', color: "#7f1d1d", marginBottom: 4 }}>
            No puedes emitir facturas todavía
          </b>
          <p style={{ margin: "4px 0 8px", color: "#991b1b", fontSize: 13, lineHeight: 1.4 }}>
            {validation.block_message || "Completa tu Perfil Fiscal y firma tu Carta Manifiesto para habilitar la facturación."}
          </p>
          <ul data-testid="fiscal-gate-checklist" style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gap: 4 }}>
            {Object.entries(GATE_LABELS).map(([key, label]) => {
              const ok = validation.gates?.[key];
              return (
                <li key={key} data-testid={`fiscal-gate-${key}`} style={{ fontSize: 12, color: ok ? "#065f46" : "#7f1d1d", fontWeight: 700 }}>
                  {ok ? "✓" : "○"} {label}
                </li>
              );
            })}
          </ul>
        </div>
        <button
          type="button"
          onClick={() => navigate("/perfil-negocio?tab=fiscal")}
          className="cash-btn primary"
          data-testid="fiscal-gate-complete-cta"
          style={{ flex: "0 0 auto", background: "#dc2626", whiteSpace: "nowrap" }}
        >
          Completar Perfil Fiscal
        </button>
      </section>
    )}
    {business && (
      <div
        className="invoice-issuer-banner"
        data-testid="billing-issuer-banner"
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "12px 16px", background: business.rfc ? "#eef2ff" : "#fef3c7",
          border: `1px solid ${business.rfc ? "#c7d2fe" : "#fde68a"}`,
          borderRadius: 14, marginBottom: 16, flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <b style={{ font: '800 13px "Nunito", sans-serif', color: "#0f172a", display: "block" }}>
            {business.rfc
              ? `Emitiendo como: ${business.business_name || business.legal_name || "Tu empresa"}`
              : "Faltan campos oficiales del SAT en tu Perfil de Negocio"}
          </b>
          <small style={{ color: "#475569" }}>
            {business.rfc ? `RFC ${business.rfc} · ${business.tax_regime || "Sin régimen"} · CP ${business.cfdi_zip || "—"}` : "Ve a Perfil de Negocio para completar Razón Social, RFC, Régimen y CP fiscal."}
          </small>
        </div>
        <a href="/perfil-negocio" data-testid="billing-edit-issuer-link" style={{ font: '800 12px "Nunito", sans-serif', color: "#4169e1" }}>
          {business.rfc ? "Editar emisor →" : "Completar ahora →"}
        </a>
      </div>
    )}
    {allowance && (
      <InvoiceAllowanceBar allowance={allowance} onAddonBought={refreshAllowance} />
    )}
    {errorDetail && <FiscalBlockModal detail={errorDetail} onClose={() => setErrorDetail(null)} />}
    <form className="billing-form" onSubmit={makeProforma} data-testid="billing-form">
      <div className="billing-form-head"><FileCheck2 /><div><h2>Datos para la factura</h2><p>Solo lo que necesita el SAT para empezar.</p></div></div>
      <div className="billing-section"><h3>Tu cliente</h3><label>Nombre o razón social<AutocompleteInput value={form.receiver_name} onChange={pickClient} options={clients.map((c) => c.name).filter(Boolean)} onCreate={createClient} createLabel="Crear cliente" placeholder="Nombre o razón social" testId="billing-client-name-input" /></label><div className="billing-two"><label>RFC<input name="receiver_rfc" value={form.receiver_rfc} onChange={update} minLength="12" maxLength="13" required data-testid="billing-client-rfc-input" /></label><label>Código postal<input name="receiver_zip" value={form.receiver_zip} onChange={update} pattern="[0-9]{5}" required data-testid="billing-client-zip-input" /></label></div><label>Correo del cliente<input name="receiver_email" type="email" value={form.receiver_email} onChange={update} required data-testid="billing-client-email-input" /></label></div>
      <div className="billing-section"><h3>Lo que vendiste</h3><label>Descripción<input name="description" value={form.description} onChange={update} required placeholder="Ej. Servicio de diseño" data-testid="billing-description-input" /></label><div className="billing-two"><label>Cantidad<input name="quantity" type="number" min="1" value={form.quantity} onChange={update} required data-testid="billing-quantity-input" /></label><label>Precio sin IVA<input name="unit_price" type="number" min="1" value={form.unit_price} onChange={update} required data-testid="billing-price-input" /></label></div></div>
      <div className="billing-estimate" data-testid="billing-estimate"><span>Total estimado con IVA</span><strong>{money.format(subtotal + iva)}</strong></div>
      {error && <p className="billing-error" data-testid="billing-form-error">{typeof error === "string" ? error : "Revisa los datos."}</p>}
      <button className="primary billing-preview-button" type="submit" disabled={!validation.ready} title={validation.ready ? "" : "Completa tu Perfil Fiscal para emitir facturas"} data-testid="show-proforma-button">Ver pro-forma <FileCheck2 size={18} /></button>
    </form>
  </div></AppShell>;
}